import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Role } from '../../types';
import {
  LayoutDashboard,
  Bell,
  CreditCard,
  FileSpreadsheet,
  Users,
  BookOpen,
  LogOut,
  MapPin,
  ShieldCheck,
  Menu,
  X,
  UserCheck,
  Clock,
  Mail,
  ChevronRight,
  User,
  ChevronDown,
  FolderArchive,
  ShoppingBag,
  Package,
  Info,
  Heart,
  Building,
  Settings,
  Award,
  ArrowRight,
  Lock,
  ThumbsUp,
  FileText,
  Trophy
} from 'lucide-react';
import { SOCIAL_LINKS } from '../../data/socialLinks';

import { formatPublicName, canUserAddChild, isSuperAdmin, hasRolePermission, isManagementOrTrainer, getHighestRole, getUserRoles } from '../../utils/formatters';

interface NavbarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isPaymentPending?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ activeTab, setActiveTab, isPaymentPending = false }) => {
  const { currentUser, loginAs, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTentangOpen, setIsTentangOpen] = useState(false);
  const [isFollowOpen, setIsFollowOpen] = useState(false);
  const followRef = React.useRef<HTMLDivElement>(null);

  // Close Follow Kami popover on outside click / Escape
  useEffect(() => {
    if (!isFollowOpen) return;
    const onDown = (e: MouseEvent) => {
      if (followRef.current && !followRef.current.contains(e.target as Node)) setIsFollowOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setIsFollowOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isFollowOpen]);


  const publicNavItems = [
    {
      id: 'tentang-parent',
      label: 'Tentang',
      icon: Info,
      dropdownItems: [
        { id: 'tentang', label: 'Tentang Persatuan', icon: Info },
        { id: 'organisasi', label: 'Carta Organisasi', icon: Users },
        { id: 'sejarah', label: 'Sejarah Perguruan', icon: Clock }
      ]
    },
    { id: 'donations', label: 'Infaq', icon: Heart },
    { id: 'silibus-awam', label: 'Silibus', icon: BookOpen },
    { id: 'dokumen', label: 'Dokumen', icon: FolderArchive },
    { id: 'merchandise', label: 'Kedai', icon: ShoppingBag },
    { id: 'hubungi', label: 'Hubungi', icon: Mail },
  ];

  const authedNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'keluarga', label: 'Keluarga', icon: Users, familyOnly: true },
    { id: 'gelanggang', label: 'Gelanggang', icon: Building },
    { id: 'syllabus', label: 'Silibus', icon: BookOpen },
    { id: 'grading', label: 'Penilaian Ujian', icon: Award, roles: ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Ketua Gelanggang', 'Superadmin', 'Jurulatih Kanan', 'AJK'] },
    { id: 'ujian-bertulis', label: 'Ujian Bertulis', icon: FileText },
    { id: 'payments', label: 'Bayaran', icon: CreditCard },
    { id: 'notices', label: 'Notis', icon: Bell },
    { id: 'audit', label: 'Audit', icon: FileSpreadsheet, roles: ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'AJK'] },
    ...(currentUser && isSuperAdmin(currentUser) ? [
      { id: 'grants', label: 'Geran', icon: ShieldCheck },
      { id: 'assets', label: 'Aset', icon: FolderArchive },
    ] : []),
    { id: 'members', label: 'Ahli', icon: Users },
    { id: 'pembelian', label: 'Pembelian Saya', icon: ShoppingBag },
    { id: 'inventory', label: 'Inventori', icon: Package, roles: ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'Ketua Gelanggang', 'AJK'] },
    { id: 'tabung', label: 'Tabung', icon: Heart, roles: ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'AJK'] },
    ...(currentUser && isSuperAdmin(currentUser) ? [{ id: 'settings', label: 'Tetapan', icon: Settings }] : []),
    { id: 'account', label: 'Akaun Saya', icon: User },
  ];




  const [isDrawerMounted, setIsDrawerMounted] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [drawerCategory, setDrawerCategory] = useState<'portal' | 'awam'>('portal');

  // Lock body scroll and trigger smooth slide in/out transition when mobile drawer menu opens/closes
  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsDrawerMounted(true);
      const timer = setTimeout(() => setIsDrawerVisible(true), 15);
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      return () => clearTimeout(timer);
    } else {
      setIsDrawerVisible(false);
      const timer = setTimeout(() => setIsDrawerMounted(false), 300);
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }
  }, [isMobileMenuOpen]);

  const handleTabClick = (id: string) => {
    setActiveTab(id);
    setIsMobileMenuOpen(false);
    if (id === 'donations' || id === 'donasi') {
      const baseTab = window.location.pathname.startsWith('/donasi') ? 'donasi' : 'donations';
      const targetPath = `/${baseTab}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState(null, '', targetPath);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }
  };

  const isPublicTab = ['landing', 'login', 'register', 'tentang', 'organisasi', 'sejarah', 'silibus-awam', 'dokumen', 'hubungi', 'merchandise', 'donations', 'donasi', 'ujian-bengkung', 'ujian'].includes(activeTab);

  const [noticeData, setNoticeData] = useState<{
    text: string;
    ctaText: string;
  }>({
    text: 'Pendaftaran Ujian Kenaikan Bengkung 2026 dibuka. Promosi awalan tamat 30 November.',
    ctaText: 'Daftar Sekarang'
  });

  useEffect(() => {
    try {
      const savedPricing = localStorage.getItem('asmm_ujian_pricing_config');
      if (savedPricing) {
        const p = JSON.parse(savedPricing);
        const freeSeats = p.freeSeatsPerPax ?? 2;
        const freeMeals = p.freeMealsPerPax ?? 2;
        const deadline = p.promoDeadline || '30 November 2026';
        setNoticeData({
          text: `Pendaftaran Ujian Kenaikan Bengkung 2026 dibuka.`,
          ctaText: 'Daftar Sekarang'
        });
      }
    } catch { }
  }, []);

  return (
    <>
      {/* Top Notice Announcement Bar - Placed above top navbar, normal flow (scrolls away, not sticky), full-width centered */}
      {isPublicTab && (
        <div className="w-full bg-slate-900 text-white py-2.5 sm:py-3 px-4 sm:px-6 shadow-xs border-b border-slate-800 print:hidden no-print">
          <div className="w-full flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-xs text-center">
            <span className="px-2.5 py-0.5 rounded-full bg-red-600 text-white font-black text-[10px] uppercase tracking-wider shrink-0">
              Notis Penting
            </span>
            <span className="font-semibold text-slate-200">
              {noticeData.text}
            </span>
            <button
              type="button"
              onClick={() => {
                handleTabClick('ujian-bengkung');
                window.history.pushState(null, '', '/ujian-bengkung');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 font-extrabold cursor-pointer transition shrink-0 group underline sm:no-underline"
            >
              <span>{noticeData.ctaText}</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      )}

      {/* Top Header Navigation Bar (Always visible with compact mobile app bar on phones, hidden on print) */}
      <header className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-xs print:hidden no-print">
        {/* Main Navbar */}
        <div className="max-w-[1340px] mx-auto px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between lg:justify-start gap-2.5 sm:gap-3 xl:gap-6">
          {/* Brand Logo & Name */}
          <a href="/" className="branding-nav cursor-pointer" onClick={(e) => { e.preventDefault(); handleTabClick('landing'); }}>
            <img
              src="/logo-pssasmm.png"
              alt="ASMM Logo"
              className="h-8 sm:h-11 w-auto object-contain shrink-0"
            />
            <div>
              <h1 className="font-extrabold text-sm sm:text-base text-slate-900 tracking-tight leading-none">
                PSSASMM
              </h1>
              <p className="hidden sm:block text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5 leading-tight whitespace-nowrap">
                <span>Awang Sulung</span>
                <span className="block">Merah Muda</span>
              </p>
            </div>
          </a>

          {/* Desktop Navigation Links (≥ 1024px) - Always visible in header */}
          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1 bg-slate-100/90 p-1 rounded-xl border border-slate-200">
            {publicNavItems.map((item) => {
              const Icon = item.icon;

              if ('dropdownItems' in item && item.dropdownItems) {
                const isSubActive = item.dropdownItems.some(sub => activeTab === sub.id);
                return (
                  <div
                    key={item.id}
                    className="relative"
                    onMouseEnter={() => setIsTentangOpen(true)}
                    onMouseLeave={() => setIsTentangOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => setIsTentangOpen(!isTentangOpen)}
                      className={`flex items-center gap-1.5 px-2.5 xl:px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${isSubActive
                        ? 'text-asmm-red font-extrabold bg-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                        }`}
                    >
                      <Icon className={`w-4 h-4 ${isSubActive ? 'text-asmm-red' : 'text-slate-400'}`} />
                      <span>{item.label}</span>
                      <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-250 ${isTentangOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isTentangOpen && (
                      <div className="absolute left-0 top-full pt-1 w-44 z-50 animate-fade-in">
                        <div className="bg-white rounded-xl border border-slate-200 shadow-lg py-1.5 space-y-0.5">
                          {item.dropdownItems.map((subItem) => {
                            const SubIcon = subItem.icon;
                            const isSubItemActive = activeTab === subItem.id;
                            const subHref = `/${subItem.id}`;
                            return (
                              <a
                                key={subItem.id}
                                href={subHref}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleTabClick(subItem.id);
                                  setIsTentangOpen(false);
                                }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs font-bold transition-colors ${isSubItemActive
                                  ? 'text-asmm-red bg-red-50/50'
                                  : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                                  }`}
                              >
                                <SubIcon className={`w-3.5 h-3.5 ${isSubItemActive ? 'text-asmm-red' : 'text-slate-400'}`} />
                                <span>{subItem.label}</span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = activeTab === item.id;
              const itemHref = item.id === 'landing' ? '/' : `/${item.id}`;
              return (
                <a
                  key={item.id}
                  href={itemHref}
                  onClick={(e) => {
                    e.preventDefault();
                    handleTabClick(item.id);
                  }}
                  className={`flex items-center gap-1.5 xl:gap-2 px-2.5 xl:px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${isActive
                    ? 'text-asmm-red font-extrabold bg-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                    }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-asmm-red' : 'text-slate-400'}`} />
                  <span className={isActive ? 'underline underline-offset-4 decoration-asmm-red decoration-2' : ''}>
                    {item.label}
                  </span>
                </a>
              );
            })}
          </nav>

          {/* Right Section: User */}
          <div className="user-actions ml-auto flex items-center gap-3">
            {/* Follow Kami - Social Media Popover */}
            <div ref={followRef} className="relative">
              <button
                id="nav-follow-kami"
                type="button"
                onClick={() => setIsFollowOpen((v) => !v)}
                aria-expanded={isFollowOpen}
                aria-haspopup="dialog"
                className={`h-9 sm:h-[46px] box-border flex items-center gap-2 px-2.5 sm:px-3.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${isFollowOpen
                  ? 'border-red-200 bg-red-50 text-red-600'
                  : 'border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                  }`}
              >
                <ThumbsUp className={`w-4 h-4 shrink-0 ${isFollowOpen ? 'text-red-600' : 'text-asmm-red'}`} />
                <span className="hidden sm:inline whitespace-nowrap">Follow @silatkulim</span>
                <ChevronDown className={`hidden sm:block w-3.5 h-3.5 text-slate-400 transition-transform ${isFollowOpen ? 'rotate-180' : ''}`} />
              </button>

              {isFollowOpen && (
                <div
                  role="dialog"
                  aria-label="Follow Kami di Media Sosial"
                  className="absolute right-0 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 top-full mt-3 z-50"
                >
                  <div className="relative animate-fade-in">
                    {/* Arrow */}
                    <div className="absolute -top-1.5 right-4 sm:right-auto sm:left-1/2 sm:-ml-1.5 w-3 h-3 rotate-45 bg-white border-l border-t border-slate-200 z-10" />
                    <div className="relative bg-white rounded-2xl border border-slate-200 shadow-xl p-4 w-max">
                      <div className="text-[11px] font-extrabold text-slate-900 uppercase tracking-wider text-center mb-3">
                        Media Sosial
                      </div>
                      <div className="flex items-center gap-2.5">
                        {SOCIAL_LINKS.map((s) => (
                          <a
                            key={s.id}
                            id={`nav-social-${s.id}`}
                            href={s.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={s.label}
                            title={s.label}
                            onClick={() => setIsFollowOpen(false)}
                            className="w-11 h-11 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700 hover:text-white hover:bg-slate-900 hover:border-slate-900 hover:-translate-y-0.5 transition-all"
                          >
                            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                              <path d={s.path} />
                            </svg>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* User Profile Badge (Desktop) */}
            {currentUser ? (
              <div className="relative user-profile">
                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="h-[46px] box-border flex items-center gap-2 px-2.5 rounded-xl hover:bg-slate-100 transition-all border border-slate-200 cursor-pointer text-left focus:outline-none"
                  title="Menu Akaun Pengguna"
                >
                  <img
                    src={currentUser.photoUrl || '/default-avatar.svg'}
                    alt={currentUser.name}
                    className="w-8 h-8 rounded-full object-cover border border-slate-200 shadow-xs shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                  />
                  <div className="text-left w-24 shrink-0 overflow-hidden">
                    <div className="text-xs font-bold text-slate-900 truncate">
                      {isPublicTab ? formatPublicName(currentUser.name) : currentUser.name}
                    </div>
                    <div className="text-[10px] text-asmm-red font-bold truncate">{getHighestRole(currentUser)}</div>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Expanded User Profile Navigation Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl border border-slate-200 shadow-xl py-2 z-50 animate-fade-in space-y-1">
                    <div className="px-4 py-2 border-b border-slate-100 space-y-0.5">
                      <div className="text-xs font-extrabold text-slate-900 truncate">
                        {isPublicTab ? formatPublicName(currentUser.name) : currentUser.name}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">{currentUser.email || currentUser.phone}</div>
                      <div className="text-[10px] text-asmm-red font-extrabold flex items-center justify-between gap-1">
                        <span>{getHighestRole(currentUser)}</span>
                        {getUserRoles(currentUser).length > 1 && (
                          <span className="text-[9px] px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded font-normal">
                            +{getUserRoles(currentUser).length - 1} peranan
                          </span>
                        )}
                      </div>
                      {getUserRoles(currentUser).length > 1 && (
                        <div className="text-[9px] text-slate-400 truncate">
                          {getUserRoles(currentUser).filter(r => r !== getHighestRole(currentUser)).join(', ')}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => { handleTabClick('dashboard'); setIsUserMenuOpen(false); }}
                      className="w-full px-4 py-2 text-left text-xs font-bold text-slate-700 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <LayoutDashboard className="w-4 h-4 text-asmm-red" />
                      <span>Dashboard</span>
                    </button>

                    {currentUser && canUserAddChild(currentUser) && (
                      <button
                        type="button"
                        onClick={() => { handleTabClick('keluarga'); setIsUserMenuOpen(false); }}
                        className="w-full px-4 py-2 text-left text-xs font-bold text-slate-700 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                      >
                        <Users className="w-4 h-4 text-asmm-red" />
                        <span>Keluarga</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => { handleTabClick('account'); setIsUserMenuOpen(false); }}
                      className="w-full px-4 py-2 text-left text-xs font-bold text-slate-700 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <User className="w-4 h-4 text-asmm-red" />
                      <span>Akaun Saya</span>
                    </button>

                    <div className="border-t border-slate-100 my-1" />

                    <button
                      type="button"
                      onClick={() => { logout(); setIsUserMenuOpen(false); }}
                      className="w-full px-4 py-2 text-left text-xs font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <LogOut className="w-4 h-4 text-rose-600" />
                      <span>Log Keluar</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => handleTabClick('login')}
                className="h-9 sm:h-[46px] box-border flex items-center justify-center px-4 sm:px-5 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl transition-all shadow-sm shrink-0 cursor-pointer active:scale-95"
              >
                Log Masuk
              </button>
            )}

            {/* Mobile User Avatar Button (< 1024px) */}
            {currentUser && (
              <button
                type="button"
                onClick={() => {
                  setDrawerCategory('portal');
                  setIsMobileMenuOpen(true);
                }}
                className="lg:hidden flex items-center p-0.5 rounded-full border border-slate-200 hover:border-asmm-red transition-colors focus:outline-none cursor-pointer shrink-0"
                title={`Profil: ${currentUser.name}`}
              >
                <img
                  src={currentUser.photoUrl || '/default-avatar.svg'}
                  alt={currentUser.name}
                  className="w-7 h-7 rounded-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                />
              </button>
            )}

            {/* Hamburger Button for Mobile / Tablet (< 1024px) - Opens Menu Laman Utama */}
            <button
              onClick={() => {
                if (!isMobileMenuOpen) setDrawerCategory(currentUser ? 'portal' : 'awam');
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              className="p-1.5 sm:p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl border border-slate-200 transition-colors lg:hidden shrink-0 cursor-pointer"
              aria-label="Menu Laman Utama"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Slide-over Mobile / Tablet Drawer Menu Overlay with Animated Slide In & Slide Out */}
      {isDrawerMounted && (
        <div className="fixed inset-0 z-50 lg:hidden flex justify-end overflow-hidden">
          {/* Backdrop with Fade In / Fade Out */}
          <div
            className={`fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${isDrawerVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>

          {/* Drawer Content with Slide In / Slide Out */}
          <div
            className={`relative w-full max-w-xs bg-white h-full shadow-2xl z-10 flex flex-col justify-between overflow-y-auto custom-scrollbar p-6 space-y-6 transform transition-transform duration-300 ease-in-out ${isDrawerVisible ? 'translate-x-0' : 'translate-x-full'
              }`}
          >
            <div>
              {/* Drawer Header */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => handleTabClick('landing')}
                >
                  <img src="/logo-pssasmm.png" alt="Logo" className="h-9 w-auto object-contain" />
                  <div>
                    <div className="font-extrabold text-sm text-slate-900">PSSASMM</div>
                    <div className="text-[10px] text-slate-500 font-bold">Menu Navigasi</div>
                  </div>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* User Profile in Mobile Drawer */}
              {currentUser ? (
                <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src={currentUser.photoUrl || '/default-avatar.svg'}
                      alt={currentUser.name}
                      className="w-10 h-10 rounded-full object-cover border border-slate-200"
                      onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                    />
                    <div>
                      <div className="text-xs font-bold text-slate-900">
                        {isPublicTab ? formatPublicName(currentUser.name) : currentUser.name}
                      </div>
                      <div className="text-[10px] text-asmm-red font-bold">{getHighestRole(currentUser)}</div>
                    </div>
                  </div>
                  <button onClick={logout} className="p-1.5 text-slate-400 hover:text-rose-600">
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleTabClick('login')}
                  className="w-full py-2.5 bg-asmm-red text-white text-xs font-bold rounded-xl mb-4 shadow-sm"
                >
                  Log Masuk Portal
                </button>
              )}

              {/* Drawer Menu Category Switcher (Asingkan Menu Lagi & Menu Laman Utama) */}
              {currentUser && (
                <div className="grid grid-cols-2 p-1 bg-slate-100 rounded-2xl border border-slate-200 mb-4 text-xs font-bold">
                  <button
                    type="button"
                    onClick={() => setDrawerCategory('portal')}
                    className={`py-2 rounded-xl transition-all flex items-center justify-center gap-1.5 ${drawerCategory === 'portal'
                      ? 'bg-white text-asmm-red shadow-xs font-extrabold'
                      : 'text-slate-600 hover:text-slate-900'
                      }`}
                  >
                    <LayoutDashboard className="w-3.5 h-3.5" />
                    <span>Portal Ahli</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDrawerCategory('awam')}
                    className={`py-2 rounded-xl transition-all flex items-center justify-center gap-1.5 ${drawerCategory === 'awam'
                      ? 'bg-white text-asmm-red shadow-xs font-extrabold'
                      : 'text-slate-600 hover:text-slate-900'
                      }`}
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    <span>Laman Utama</span>
                  </button>
                </div>
              )}

              {/* View 1: Menu Lagi (Portal Ahli Links) */}
              {currentUser && drawerCategory === 'portal' && (
                <div className="space-y-4">
                  <div className="space-y-1">
                    <div className="text-[11px] font-bold text-slate-500 uppercase mb-1 px-1">Menu Portal Ahli (Lagi):</div>
                    {authedNavItems.map((item) => {
                      let isAllowed = hasRolePermission(currentUser, item.roles);
                      if ('familyOnly' in item && item.familyOnly) {
                        isAllowed = !!(currentUser && canUserAddChild(currentUser));
                      }
                      if (!isAllowed) return null;
                      const Icon = item.icon;
                      if (item.id === 'syllabus' && isManagementOrTrainer(currentUser)) {
                        const isAnyActive = activeTab === 'syllabus' || activeTab === 'pengurusan-silibus';
                        return (
                          <div key={item.id} className="space-y-1">
                            <button
                              onClick={() => handleTabClick('syllabus')}
                              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${isAnyActive
                                ? 'text-asmm-red bg-red-50/60 font-extrabold'
                                : 'text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                              <Icon className={`w-4 h-4 shrink-0 ${isAnyActive ? 'text-asmm-red' : 'text-slate-500'}`} />
                              <span>{item.label}</span>
                            </button>
                            <div className="pl-3.5 ml-4 border-l-2 border-slate-200 space-y-1 py-0.5">
                              <button
                                onClick={() => handleTabClick('syllabus')}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${activeTab === 'syllabus'
                                  ? 'text-asmm-red bg-red-100/70 font-black'
                                  : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                <BookOpen className="w-3.5 h-3.5 text-slate-400" />
                                <span>Pengajian Silibus</span>
                              </button>
                              <button
                                onClick={() => handleTabClick('pengurusan-silibus')}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${activeTab === 'pengurusan-silibus'
                                  ? 'text-asmm-red bg-red-100/70 font-black'
                                  : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                <Settings className="w-3.5 h-3.5 text-slate-400" />
                                <span>Pengurusan Silibus</span>
                              </button>
                            </div>
                          </div>
                        );
                      }

                      if (item.id === 'grading') {
                        const isAnyActive = activeTab === 'grading' || activeTab === 'grading-settings' || activeTab === 'ujian-grading' || activeTab === 'grading-leaderboard' || activeTab === 'ujian-bertulis';
                        return (
                          <div key={item.id} className="space-y-1">
                            <button
                              onClick={() => handleTabClick('grading')}
                              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${isAnyActive
                                ? 'text-asmm-red bg-red-50/60 font-extrabold'
                                : 'text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                              <Icon className={`w-4 h-4 shrink-0 ${isAnyActive ? 'text-asmm-red' : 'text-slate-500'}`} />
                              <span>{item.label}</span>
                            </button>
                            <div className="pl-3.5 ml-4 border-l-2 border-slate-200 space-y-1 py-0.5">
                              <button
                                onClick={() => handleTabClick('grading')}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${activeTab === 'grading' || activeTab === 'ujian-grading'
                                  ? 'text-asmm-red bg-red-100/70 font-black'
                                  : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                <Award className="w-3.5 h-3.5 text-slate-400" />
                                <span>Pentaksiran Calon</span>
                              </button>
                              <button
                                onClick={() => handleTabClick('ujian-bertulis')}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${activeTab === 'ujian-bertulis'
                                  ? 'text-asmm-red bg-red-100/70 font-black'
                                  : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                <FileText className="w-3.5 h-3.5 text-slate-400" />
                                <span>Ujian Bertulis</span>
                              </button>
                              <button
                                onClick={() => handleTabClick('grading-leaderboard')}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${activeTab === 'grading-leaderboard'
                                  ? 'text-asmm-red bg-red-100/70 font-black'
                                  : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                <Trophy className="w-3.5 h-3.5 text-slate-400" />
                                <span>Leaderboard</span>
                              </button>
                              {isManagementOrTrainer(currentUser) && (
                                <button
                                  onClick={() => handleTabClick('grading-settings')}
                                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${activeTab === 'grading-settings'
                                    ? 'text-asmm-red bg-red-100/70 font-black'
                                    : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                >
                                  <Settings className="w-3.5 h-3.5 text-slate-400" />
                                  <span>Tetapan</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      }

                      if (item.id === 'members' && isSuperAdmin(currentUser)) {
                        const isAnyActive = activeTab === 'members' || activeTab === 'roles';
                        return (
                          <div key={item.id} className="space-y-1">
                            <button
                              onClick={() => handleTabClick('members')}
                              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${isAnyActive
                                ? 'text-asmm-red bg-red-50/60 font-extrabold'
                                : 'text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                              <Icon className={`w-4 h-4 shrink-0 ${isAnyActive ? 'text-asmm-red' : 'text-slate-500'}`} />
                              <span>{item.label}</span>
                            </button>
                            <div className="pl-3.5 ml-4 border-l-2 border-slate-200 space-y-1 py-0.5">
                              <button
                                onClick={() => handleTabClick('members')}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${activeTab === 'members'
                                  ? 'text-asmm-red bg-red-100/70 font-black'
                                  : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                <Users className="w-3.5 h-3.5 text-slate-400" />
                                <span>Direktori Ahli</span>
                              </button>
                              <button
                                onClick={() => handleTabClick('roles')}
                                className={`w-full flex items-center justify-between px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${activeTab === 'roles'
                                  ? 'text-asmm-red bg-red-100/70 font-black'
                                  : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                <div className="flex items-center gap-2.5 truncate">
                                  <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Roles Management</span>
                                </div>
                              </button>
                            </div>
                          </div>
                        );
                      }

                      const isActive = activeTab === item.id;
                      const isLockedByPayment = isPaymentPending && !['dashboard', 'payments', 'account'].includes(item.id);
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleTabClick(item.id)}
                          className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all ${isActive
                            ? 'text-asmm-red bg-red-50/60 font-extrabold'
                            : isLockedByPayment
                              ? 'text-slate-400 hover:bg-slate-100 opacity-60'
                              : 'text-slate-700 hover:bg-slate-100'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-asmm-red' : isLockedByPayment ? 'text-slate-400' : 'text-slate-500'}`} />
                            <span className={isActive ? 'underline underline-offset-4 decoration-asmm-red decoration-2' : ''}>
                              {item.label}
                            </span>
                          </div>
                          {isLockedByPayment && (
                            <Lock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* View 2: Menu Laman Utama (Awam & Info) */}
              {(!currentUser || drawerCategory === 'awam') && (
                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-500 uppercase mb-1 px-1">Menu Laman Utama:</div>
                  {publicNavItems.map((item) => {
                    const Icon = item.icon;

                    if ('dropdownItems' in item && item.dropdownItems) {
                      return (
                        <div key={item.id} className="space-y-1">
                          <div className="flex items-center gap-3 px-3.5 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">
                            <Icon className="w-3.5 h-3.5 text-slate-400" />
                            <span>{item.label}</span>
                          </div>
                          <div className="pl-3 border-l border-slate-100 ml-5 space-y-1">
                            {item.dropdownItems.map((subItem) => {
                              const SubIcon = subItem.icon;
                              const isSubActive = activeTab === subItem.id;
                              const subHref = `/${subItem.id}`;
                              return (
                                <a
                                  key={subItem.id}
                                  href={subHref}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleTabClick(subItem.id);
                                  }}
                                  className={`w-full flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${isSubActive
                                    ? 'text-asmm-red bg-red-50/60 font-extrabold'
                                    : 'text-slate-700 hover:bg-slate-100'
                                    }`}
                                >
                                  <SubIcon className={`w-3.5 h-3.5 shrink-0 ${isSubActive ? 'text-asmm-red' : 'text-slate-500'}`} />
                                  <span className={isSubActive ? 'underline underline-offset-4 decoration-asmm-red decoration-2' : ''}>
                                    {subItem.label}
                                  </span>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }

                    const isActive = activeTab === item.id;
                    const itemHref = item.id === 'landing' ? '/' : `/${item.id}`;
                    return (
                      <a
                        key={item.id}
                        href={itemHref}
                        onClick={(e) => {
                          e.preventDefault();
                          handleTabClick(item.id);
                        }}
                        className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all ${isActive
                          ? 'text-asmm-red bg-red-50/60 font-extrabold'
                          : 'text-slate-700 hover:bg-slate-100'
                          }`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-asmm-red' : 'text-slate-500'}`} />
                        <span className={isActive ? 'underline underline-offset-4 decoration-asmm-red decoration-2' : ''}>
                          {item.label}
                        </span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ergonomic Bottom Mobile Navigation Bar for Mobile Phone (< 640px) - Only for Logged In Users inside Portal Pages */}
      {currentUser && !isPublicTab && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-200 sm:hidden shadow-lg pb-safe">
          <div className="grid grid-cols-4 gap-1 p-1">
            <button
              onClick={() => handleTabClick('dashboard')}
              className={`flex flex-col items-center justify-center py-1.5 rounded-xl text-[10px] font-bold transition-all cursor-pointer ${activeTab === 'dashboard' ? 'text-asmm-red bg-red-50/70 font-black' : 'text-slate-500 hover:text-slate-800'
                }`}
            >
              <LayoutDashboard className="w-4 h-4 mb-0.5" />
              <span>Dashboard</span>
            </button>

            <button
              onClick={() => handleTabClick('notices')}
              className={`flex flex-col items-center justify-center py-1.5 rounded-xl text-[10px] font-bold transition-all cursor-pointer ${activeTab === 'notices' ? 'text-asmm-red bg-red-50/70 font-black' : 'text-slate-500 hover:text-slate-800'
                }`}
            >
              <Bell className="w-4 h-4 mb-0.5" />
              <span>Notis</span>
            </button>

            <button
              onClick={() => handleTabClick('payments')}
              className={`flex flex-col items-center justify-center py-1.5 rounded-xl text-[10px] font-bold transition-all cursor-pointer ${activeTab === 'payments' ? 'text-asmm-red bg-red-50/70 font-black' : 'text-slate-500 hover:text-slate-800'
                }`}
            >
              <CreditCard className="w-4 h-4 mb-0.5" />
              <span>Bayar</span>
            </button>

            <button
              onClick={() => {
                setDrawerCategory('portal');
                setIsMobileMenuOpen(true);
              }}
              className={`flex flex-col items-center justify-center py-1.5 rounded-xl text-[10px] font-bold transition-all cursor-pointer ${!['dashboard', 'notices', 'payments'].includes(activeTab) ? 'text-asmm-red bg-red-50/70 font-black' : 'text-slate-500 hover:text-slate-800'
                }`}
            >
              <Menu className="w-4 h-4 mb-0.5" />
              <span>Lagi</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
